import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormCaptureFields } from './types';

interface VSLeadsTimerOverlayProps {
  config: FormCaptureFields;
  onSubmit: () => void;
  isVisible: boolean;
}

export const VSLeadsTimerOverlay: React.FC<VSLeadsTimerOverlayProps> = ({
  config,
  onSubmit,
  isVisible
}) => {
  useLanguage();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    whatsapp: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isVisible) return null;

  const { timerFormPosition, cores, titulo, rodape, showTitle, showFooter, showBackground, content } = config;
  const buttonText = content?.buttonText || 'Liberar Acesso';
  const camposCaptura = config.camposCaptura || config.fields?.map(f => f.id) || [];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (camposCaptura.includes('nome') || camposCaptura.includes('firstName') && !formData.nome) {
      newErrors.nome = translateInterfaceText("Nome é obrigatório");
    }

    if (camposCaptura.includes('email')) {
      if (!formData.email) {
        newErrors.email = translateInterfaceText("E-mail é obrigatório");
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = translateInterfaceText("E-mail inválido");
      }
    }

    if (camposCaptura.includes('whatsapp') && formData.whatsapp) {
      if (!/^[0-9]{10,11}$/.test(formData.whatsapp.replace(/\D/g, ''))) {
        newErrors.whatsapp = translateInterfaceText("WhatsApp inválido");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Store in localStorage to prevent showing again
      localStorage.setItem(`vsleads-form-${config.videoId}-submitted`, 'true');
      
      // Call the onSubmit callback to resume video playback
      onSubmit();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine positioning classes based on timerFormPosition
  const positionClasses = timerFormPosition === 'center' 
    ? 'inset-0 flex items-center justify-center'
    : 'inset-y-0 right-0 w-full md:w-80 flex items-center';

  return (
    <div className={`fixed ${positionClasses} z-50 bg-black bg-opacity-60`}>
      <div 
        className={`p-6 rounded-lg shadow-lg max-w-md w-full mx-4 ${showBackground ? 'bg-white' : 'bg-transparent'}`}
        style={showBackground ? { backgroundColor: cores.background } : {}}
      >
        {showTitle && (
          <h3 className="text-lg font-medium mb-4 text-center">{titulo || translateInterfaceText("🚀 Para continuar assistindo, preencha seus dados rápidos abaixo!")}</h3>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {(camposCaptura.includes('nome') || camposCaptura.includes('firstName')) && (
            <div>
              <Label htmlFor="nome">{translateInterfaceText("Nome")}</Label>
              <Input
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                className={errors.nome ? 'border-red-500' : ''}
              />
              {errors.nome && <p className="text-red-500 text-sm mt-1">{errors.nome}</p>}
            </div>
          )}
          
          {camposCaptura.includes('email') && (
            <div>
              <Label htmlFor="email">{translateInterfaceText("E-mail")}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>
          )}
          
          {camposCaptura.includes('whatsapp') && (
            <div>
              <Label htmlFor="whatsapp">{translateInterfaceText("WhatsApp")}</Label>
              <Input
                id="whatsapp"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleInputChange}
                className={errors.whatsapp ? 'border-red-500' : ''}
                placeholder={translateInterfaceText("(XX) XXXXX-XXXX")}
              />
              {errors.whatsapp && <p className="text-red-500 text-sm mt-1">{errors.whatsapp}</p>}
            </div>
          )}
          
          <Button
            type="submit"
            className="w-full mt-4"
            disabled={isSubmitting}
            style={{
              backgroundColor: cores.botao,
              color: cores.textoBotao
            }}
          >
            {isSubmitting ? translateInterfaceText("Enviando...") : buttonText}
          </Button>
        </form>
        
        {showFooter && rodape && (
          <p className="text-sm text-center mt-4 text-gray-500">{rodape}</p>
        )}
      </div>
    </div>
  );
};
